// Phase SIKA-5c/C + 5e/4 — Panel 5: SIKA Chat (inline).
//
// Source: §04 Table 13 + canonical prototype `screens/sika.jsx` SikaChat.
// Video walkthrough frame 22 (Panel 5 of 5).
//
// Lean inline panel at the bottom of the Goal Layer page. After 5e/4
// it uses the same v2 message renderer + composer as /sika/chat, so
// the visual language is consistent across surfaces. The standalone
// full workspace remains accessible at /sika/chat.
//
// Doctrine:
//   N11 — Chat is ALWAYS rendered as the 5th panel. DOM-order enforced
//         by sika5Layout.test.tsx.
//   N12 — Tier-gated: free users get a 5/week soft cap pill; pro
//         unlimited. Cap is advisory, not blocking (promotion-not-
//         restriction copy).

import { useEffect, useMemo, useRef } from "react";

import { Eyebrow } from "../super-acca";
import { useSikaChat } from "../../sika/useSikaChat.ts";
import { SikaComposer } from "../../pages/sika/chat-v2/SikaComposer.tsx";
import { SikaMessageV2 } from "../../pages/sika/chat-v2/SikaMessage.tsx";

export interface SikaChatPanelProps {
  /** Active goal id from the Goal Layer page — pre-wires the chat to
   * that goal so messages thread correctly without a separate selector. */
  goalId?: string | null;
  /** Tier flag. Free shows 5/week soft cap pill in the composer. */
  pro?: boolean;
}

export function SikaChatPanel({
  goalId = null,
  pro = false,
}: SikaChatPanelProps): JSX.Element {
  const chat = useSikaChat();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Pre-wire to the active Goal Layer goal whenever it changes.
  useEffect(() => {
    if (goalId && chat.goalId !== goalId) {
      chat.setGoalId(goalId);
    }
  }, [goalId, chat]);

  // Auto-scroll on new tokens / messages when near bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    if (isNearBottom) el.scrollTop = el.scrollHeight;
  }, [chat.messages.length, chat.sending, chat.streamingText]);

  const visible = chat.messages.filter(
    (m) => m.role !== "tool" || m.card_type != null,
  );

  // Weekly user-message count for the soft cap.
  const weekUserMessageCount = useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return chat.messages.filter(
      (m) =>
        m.role === "user" && new Date(m.created_at).getTime() >= sevenDaysAgo,
    ).length;
  }, [chat.messages]);

  return (
    <div
      className="sika-chat-2"
      style={{ padding: 18 }}
      data-panel="sika-chat"
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <Eyebrow>Panel 5 · SIKA Chat</Eyebrow>
        <div style={{ marginLeft: "auto" }}>
          <a
            href="/sika/chat"
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
              textDecoration: "none",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderBottom: "1px solid var(--sa-hairline)",
              paddingBottom: 1,
            }}
            data-action="open-full-chat"
          >
            Open full chat →
          </a>
        </div>
      </header>

      <div
        ref={scrollRef}
        style={{
          border: "1px solid var(--sa-hairline)",
          borderRadius: 3,
          background: "var(--sa-bg-base)",
          height: 360,
          overflowY: "auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
        role="log"
        aria-label="SIKA conversation"
      >
        {visible.length === 0 && !chat.sending ? (
          <div
            style={{
              margin: "auto",
              textAlign: "center",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              color: "var(--sa-fg-muted)",
              maxWidth: 320,
            }}
          >
            Ask SIKA anything about this goal — projections, what-if scenarios,
            recalibration, or just a status check.
          </div>
        ) : (
          visible.map((m) => (
            <SikaMessageV2
              key={m.id}
              msg={m}
              onAction={(t) => void chat.send(t)}
              sending={chat.sending}
            />
          ))
        )}
        {chat.sending ? (
          <SikaMessageV2
            msg={{
              id: "__streaming__",
              role: "assistant",
              content: chat.streamingText,
              tool_use:
                chat.agentSteps.length > 0
                  ? { agent_steps: chat.agentSteps }
                  : null,
              card_type: null,
              created_at: new Date().toISOString(),
            }}
            streaming
          />
        ) : null}
      </div>

      <div style={{ marginTop: 10 }}>
        <SikaComposer
          onSend={(text) => void chat.send(text)}
          disabled={chat.sending}
          pro={pro}
          weeklyUserMessageCount={weekUserMessageCount}
          placeholder="Message SIKA"
        />
      </div>
    </div>
  );
}
